import { useEffect, useMemo, useState, type SyntheticEvent } from 'react';
import { useNavigate } from 'react-router';
import {
  CASE_SORT,
  ORIGINS,
  ORIGIN_LABEL,
  daysUntilPurge,
  pendingDeletionActions,
  type DateRange,
  type SortPreset,
} from '@/domain';
import { useArchive } from '@/application/usecases/useArchive';
import { useTableOrder } from '@/application/usecases/useTableOrder';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import {
  useApproveDeletion,
  usePurgeCase,
  useRestoreCase,
  useWithdrawDeletion,
} from '@/application/usecases/useOrders';
import { useSession } from '@/application/usecases/useSession';
import type { DeletedCaseRow } from '@/application/ports/ArchiveRepository';
import { formatDay } from '@/shared/format/instant';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { CaseTable } from '@/ui/data/CaseTable';
import { PAGE_SIZES, useTablePage } from '@/application/usecases/useTablePage';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import type { Column } from '@/ui/data/ResponsiveTable';
import { SortSelect } from '@/ui/data/SortSelect';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { StatusDot } from '@/ui/data/StatusDot';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { PurgeCaseDialog } from './PurgeCaseDialog';
import styles from './ArchiveScreen.module.css';

/**
 * The archive's Deleted tab: every case with a deletion on it.
 *
 * The ones waiting for approval come first, with Approve and Refuse for somebody
 * who may decide them; the deleted follow, most recently deleted first, each
 * saying how long it has before it is purged, with Restore for a holder of
 * approveDeletion, and Delete permanently for the same person, which asks first
 * and then does what the purge day would have done. Request of 24 September
 * 2026. Filtered on the server, as the delivered archive is.
 */
export function DeletedCases() {
  const navigate = useNavigate();
  const session = useSession();
  const permissions = session.data?.permissions ?? [];
  const userId = session.data?.user.id ?? '';

  const [q, setQ] = useState('');
  const [range, setRange] = useState<DateRange>({ from: '', to: '' });
  const [waitingOnly, setWaitingOnly] = useState(false);
  const [origin, setOrigin] = useState('all');

  // A page at a time, from the server. Section 10 of the request of
  // 16 September 2026.
  const paging = useTablePage('deleted');
  // The columns as this reader laid them out, kept on their account. The request of 7 October 2026, section 3.
  const layout = useTableLayout('deleted');

  const filter = useMemo(
    () => ({
      set: 'deleted' as const,
      q,
      from: range.from,
      to: range.to,
      ...(waitingOnly ? { deletionStatus: 'pending' as const } : {}),
      origin,
      page: paging.page,
      pageSize: paging.pageSize,
    }),
    [q, range, waitingOnly, origin, paging.page, paging.pageSize],
  );

  const { data, isPending, isError } = useArchive(filter);
  const approve = useApproveDeletion();
  const refuse = useWithdrawDeletion();
  const restore = useRestoreCase();
  const purge = usePurgeCase();
  // Each decision in a row is asked about first. The request of 5 October 2026 (third), section 2.
  const ask = useConfirmAction();
  const busy = approve.isPending || refuse.isPending || restore.isPending || purge.isPending;

  // Delete permanently asks first. One dialog for the table, over whichever row
  // asked, rather than one inside every row.
  const [purging, setPurging] = useState<DeletedCaseRow | null>(null);

  const today = formatDay(new Date().toISOString());
  const rows = data?.items ?? [];
  const filtering =
    q.trim() !== '' || range.from !== '' || range.to !== '' || waitingOnly || origin !== 'all';

  // A filter makes a different list, and page three of the old one is nothing
  // in the new one.
  const { reset } = paging;
  useEffect(() => {
    reset();
  }, [reset, q, range, waitingOnly, origin]);

  const filters: FilterDef[] = [
    {
      kind: 'search',
      label: strings.deletion.search,
      placeholder: strings.deletion.searchPlaceholder,
      value: q,
      onChange: setQ,
    },
    {
      // As on the other two tabs. Request of 5 October 2026.
      kind: 'select',
      label: strings.archive.origin,
      value: origin,
      options: [
        { value: 'all', label: strings.archive.allOrigins },
        ...ORIGINS.map((key) => ({ value: key, label: ORIGIN_LABEL[key] })),
      ],
      onChange: setOrigin,
    },
    {
      kind: 'dateRange',
      label: strings.deletion.deletedOn,
      from: range.from,
      to: range.to,
      onChange: setRange,
    },
    {
      kind: 'toggle',
      label: strings.deletion.waitingOnly,
      value: waitingOnly,
      onChange: setWaitingOnly,
    },
  ];

  /*
   * Newest first unless the reader chose otherwise: request of 5 October 2026,
   * section 2. The waiting deletions first, then the most recently deleted, is
   * the order the server sends and the order this screen used to open in; it is
   * still offered, and a preset with no comparator leaves the rows exactly as
   * they arrived. Section 3.2 of the request of 13 September 2026.
   */
  const presets = useMemo<SortPreset<DeletedCaseRow>[]>(
    () => [
      { key: 'newestFirst', label: strings.table.newestFirst, compare: CASE_SORT.newestFirst },
      { key: 'waitingFirst', label: strings.table.waitingFirst },
      { key: 'doctorAtoZ', label: strings.table.doctorAtoZ, compare: CASE_SORT.doctorAtoZ },
    ],
    [],
  );
  const order = useTableOrder('archive:deleted', presets);

  /** What a row's action says when it lands. A refusal is said by whoever asked the question. */
  const settled = (row: DeletedCaseRow, done: string) => () => {
    toast(`${row.jobOrderNo} ${done}`);
  };

  const columns: Column<DeletedCaseRow>[] = [
    { key: 'product', label: strings.archive.item, sortable: true, render: (row) => row.product },
    {
      key: 'deletedOn',
      label: strings.deletion.deletedOn,
      mono: true,
      nowrap: true,
      sortable: true,
      sortValue: (row) => row.deletion.deletedAt ?? row.deletion.requestedAt ?? '',
      // While it waits, the day it was asked for.
      render: (row) => {
        const moment = row.deletion.deletedAt ?? row.deletion.requestedAt;
        return moment === null ? '' : formatDay(moment);
      },
    },
    {
      key: 'deletedBy',
      label: strings.deletion.deletedBy,
      render: (row) =>
        row.deletion.status === 'pending'
          ? `${strings.deletion.askedBy} ${row.deletion.requestedByUserName ?? ''}`
          : (row.deletion.decidedByUserName ?? ''),
    },
    {
      key: 'purgesIn',
      label: strings.deletion.purgesIn,
      nowrap: true,
      sortable: true,
      sortValue: (row) =>
        row.deletion.purgesAt === null ? Number.MAX_SAFE_INTEGER : daysUntilPurge(row.deletion.purgesAt, today),
      render: (row) => {
        if (row.deletion.purgesAt === null) return strings.deletion.notScheduled;
        const days = daysUntilPurge(row.deletion.purgesAt, today);
        if (days === 0) return strings.deletion.purgesToday;
        return `${days} ${days === 1 ? strings.deletion.day : strings.deletion.days}`;
      },
    },
    // What the rows carry and the table does not show as it comes. The request of 8 October 2026 (second).
    {
      key: 'reason',
      label: strings.table.columns.reason,
      optional: true,
      render: (row) => row.deletion.reason ?? '',
    },
    {
      key: 'requestedBy',
      label: strings.table.columns.requestedBy,
      sortable: true,
      optional: true,
      sortValue: (row) => row.deletion.requestedByUserName ?? '',
      render: (row) => row.deletion.requestedByUserName ?? '',
    },
  ];

  /** A button in a row that opens a record when tapped: its own tap must not open it too. */
  const inRow = (event: SyntheticEvent) => event.stopPropagation();

  return (
    <>
      <FilterBar
        label={strings.deletion.filtersLabel}
        filters={filters}
        sort={<SortSelect presets={presets} value={order.presetKey} onChange={order.choose} />}
      />

      {isError && <p className={styles.hint}>{strings.errors.server}</p>}

      {!isError &&
        (isPending ? (
          <SkeletonRows rows={4} label={strings.deletion.loading} />
        ) : (
          <CaseTable
            customize={layout}
            columns={columns}
            rows={order.apply(rows)}
            sort={order.column}
            onSortChange={order.sortByColumn}
            identity={(row) => ({
              jobOrderNo: row.jobOrderNo,
              dentistName: row.dentistName,
              patientName: row.patientName,
            })}
            status={(row) => {
              const pending = row.deletion.status === 'pending';
              const actions = pendingDeletionActions(row.deletion, userId, permissions);
              const command = { jobOrderNo: row.jobOrderNo, rowVersion: row.rowVersion };

              return (
                <span className={styles.deletedStatus} onClick={inRow} onKeyDown={inRow}>
                  <StatusDot tone={pending ? 'watch' : 'hold'}>
                    {pending ? strings.deletion.awaitingApproval : strings.deletion.deleted}
                  </StatusDot>
                  {pending && actions.approve && (
                    <Button
                      variant="primary"
                      disabled={busy}
                      onClick={() =>
                        ask(
                          strings.confirm.approveDeletion(row.jobOrderNo),
                          () =>
                            approve
                              .mutateAsync(command)
                              .then(settled(row, strings.deletion.approvedToast)),
                          { confirmLabel: strings.deletion.approve },
                        )
                      }
                    >
                      {strings.deletion.approve}
                    </Button>
                  )}
                  {pending && (actions.refuse || actions.withdraw) && (
                    <Button
                      disabled={busy}
                      onClick={() =>
                        ask(
                          actions.withdraw
                            ? strings.confirm.withdrawDeletion(row.jobOrderNo)
                            : strings.confirm.refuseDeletion(row.jobOrderNo),
                          () =>
                            refuse
                              .mutateAsync(command)
                              .then(
                                settled(
                                  row,
                                  actions.withdraw
                                    ? strings.deletion.withdrawnToast
                                    : strings.deletion.refusedToast,
                                ),
                              ),
                          {
                            confirmLabel: actions.withdraw
                              ? strings.deletion.withdraw
                              : strings.deletion.refuse,
                          },
                        )
                      }
                    >
                      {actions.withdraw ? strings.deletion.withdraw : strings.deletion.refuse}
                    </Button>
                  )}
                  {!pending && permissions.includes('approveDeletion') && (
                    <Button
                      disabled={busy}
                      onClick={() =>
                        ask(
                          strings.confirm.restoreCase(row.jobOrderNo),
                          () =>
                            restore
                              .mutateAsync(command)
                              .then(settled(row, strings.deletion.restoredToast)),
                          { confirmLabel: strings.deletion.restore },
                        )
                      }
                    >
                      {strings.deletion.restore}
                    </Button>
                  )}
                  {!pending && permissions.includes('approveDeletion') && (
                    <Button disabled={busy} onClick={() => setPurging(row)}>
                      {strings.deletion.purge}
                    </Button>
                  )}
                </span>
              );
            }}
            statusSortValue={(row) => row.deletion.status}
            paging={{
              page: paging.page,
              pageSize: paging.pageSize,
              total: data?.total ?? 0,
              sizes: PAGE_SIZES,
              onPage: paging.goTo,
              onPageSize: paging.choose,
              label: strings.table.pagesLabel,
            }}
            rowKey={(row) => row.jobOrderNo}
            caption={strings.deletion.caption}
            emptyHeading={filtering ? strings.table.noMatch : strings.deletion.emptyHeading}
            emptyDetail={filtering ? strings.table.noMatchDetail : strings.deletion.emptyDetail}
            onRowClick={(row) => void navigate(`/archive/${row.jobOrderNo}`)}
          />
        ))}

      {purging !== null && (
        <PurgeCaseDialog
          jobOrderNo={purging.jobOrderNo}
          busy={purge.isPending}
          error={purge.error === null ? null : failureWords(purge.error)}
          onCancel={() => {
            setPurging(null);
            purge.reset();
          }}
          onConfirm={() => {
            const row = purging;
            purge.mutate(
              { jobOrderNo: row.jobOrderNo, rowVersion: row.rowVersion },
              {
                // A refusal stays in the dialog, where the choice was made.
                onSuccess: () => {
                  setPurging(null);
                  toast(`${row.jobOrderNo} ${strings.deletion.purgedToast}`);
                },
              },
            );
          }}
        />
      )}
    </>
  );
}
